import type { CatalogItem, CategoryItem, FormularyItem, PrintMenuView, PrintNote, PrintPaper, PrintSheet, RecipeLine, Route } from "@vaulted/shared";
import { DOSE_UNIT_LABELS, PRINT_PAPERS, componentDoses, doseToMl, formatDose } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { errorMessage, get, put } from "../api";
import { Alert, Button, Checkbox, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, TabButtons, money } from "../components/ui";
import { useSession } from "../session";
import { type CatalogResponse, useCatalog } from "./Catalog";
import { RemoveButton } from "./settings/common";
import { BackLink } from "../components/BackLink";

/** Screen pixels per inch (CSS). */
const PX = 96;
const num = new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 });

/** Sorting name: "The Ascent" files under A. */
const alpha = (name: string) => name.replace(/^the\s+/i, "").toLowerCase();

/** Services and add-ons on a sheet, by category, then A to Z within each. */
function sheetItems(sheet: PrintSheet, data: CatalogResponse) {
  return data.categories
    .filter((c) => sheet.categoryIds.includes(c.id))
    .map((c) => ({
      category: c,
      items: data.items
        .filter((i) => i.categoryId === c.id && i.status === "active" && (i.type === "service" || i.type === "add_on") && (sheet.includeHidden || i.showOnPublicMenu))
        .sort((a, b) => alpha(a.name).localeCompare(alpha(b.name), undefined, { numeric: true })),
    }))
    .filter((g) => g.items.length > 0);
}

/** A starting layout when none is saved: one-per-visit categories (drips) on the big sheet, the rest on letter. */
function suggested(data: CatalogResponse): PrintSheet[] {
  const used = data.categories.filter((c) => data.items.some((i) => i.categoryId === c.id && i.status === "active" && (i.type === "service" || i.type === "add_on")));
  const drips = used.filter((c) => c.onePerVisit).map((c) => c.id);
  const rest = used.filter((c) => !c.onePerVisit).map((c) => c.id);
  const sheet = (name: string, paper: PrintPaper, landscape: boolean, columns: number, categoryIds: string[]): PrintSheet => ({
    id: crypto.randomUUID(),
    name,
    paper,
    landscape,
    columns,
    categoryIds,
    includeHidden: false,
    notes: [],
  });
  return [sheet("Drips", "13x19", true, 3, drips), sheet("Boosts and shots", "letter", false, 1, rest)].filter((s) => s.categoryIds.length > 0);
}

/** Whether a sheet lists what's in each mix; by default only drip sheets do. */
/** A drip: a service given in a bag of fluids (not the Additional Fluids add-on). */
const isDrip = (item: CatalogItem, formulary: FormularyItem[]) => item.type === "service" && item.recipe.some((l) => formulary.find((f) => f.id === l.formularyItemId)?.kind === "fluid");

/** Whether a sheet lists what's in each mix; by default only sheets with drips do. */
const showsMixes = (s: PrintSheet, data: CatalogResponse) => s.showMixes ?? sheetItems(s, data).some((g) => g.items.some((i) => isDrip(i, data.formulary)));

const size = (s: PrintSheet) => {
  const p = PRINT_PAPERS[s.paper];
  return s.landscape ? { w: p.height, h: p.width } : { w: p.width, h: p.height };
};

/**
 * Catalog → Print menu: the nurses' menu, printed from the live catalog (prices, recipes, doses,
 * routes), one sheet at a time. Also lists stock that isn't on the menu.
 */
export function PrintMenuPage() {
  const catalog = useCatalog();
  const view = useQuery({
    queryKey: ["print-menu"],
    queryFn: () => get<PrintMenuView>("/api/catalog/print-menu"),
  });
  if (catalog.isLoading || view.isLoading) return <Loading />;
  if (!catalog.data || !view.data) return <Alert>{errorMessage(catalog.error ?? view.error)}</Alert>;
  return <PrintMenu data={catalog.data} view={view.data} />;
}

function PrintMenu({ data, view }: { data: CatalogResponse; view: PrintMenuView }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const manage = can("catalog.manage");
  const saved = view.sheets.length > 0;
  const [sheets, setSheets] = useState<PrintSheet[]>(() => (saved ? view.sheets : suggested(data)));
  const [at, setAt] = useState(0);
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: () => put<PrintMenuView>("/api/catalog/print-menu", { sheets }),
    onSuccess: (v) => {
      qc.setQueryData(["print-menu"], v);
      setEditing(false);
    },
  });
  const dirty = !saved || JSON.stringify(sheets) !== JSON.stringify(view.sheets);
  const sheet = sheets[Math.min(at, sheets.length - 1)];
  const { w, h } = sheet ? size(sheet) : { w: 0, h: 0 };
  // Text size: grows while the sheet has room, shrinks (to 70%) when it runs over; starts again when anything changes.
  const [fontScale, setFontScale] = useState(1);
  const settled = useRef(false);
  const fitKey = JSON.stringify([sheet, data]);
  useLayoutEffect(() => {
    settled.current = false;
    setFontScale(1);
  }, [fitKey]);

  return (
    <div>
      <div className="print:hidden">
        <PageHeader
          title="Print menu"
          description="The nurses' menu, printed from the catalog: prices, what's in each, and doses. Change a recipe and the next print shows it."
          action={
            <BackLink to="/catalog/services" label="Catalog" className="text-sm font-semibold text-brand" />
          }
        />
        {!saved && (
          <Alert tone="info">
            {sheets.length ? "A suggested layout, not saved yet." : "No sheets yet."} {manage ? "Change it with Edit sheets, then save." : "A manager can set up the sheets."}
          </Alert>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {sheets.length > 0 && (
            <TabButtons
              label="Sheets"
              items={sheets.map((s, i) => [String(i), `${s.name} · ${PRINT_PAPERS[s.paper].label.replace(/ \(.*\)/, "")}`] as const)}
              value={String(Math.min(at, sheets.length - 1))}
              onChange={(k) => setAt(Number(k))}
            />
          )}
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          {sheet && (
            <Button variant="primary" onClick={() => window.print()}>
              Print this sheet
            </Button>
          )}
          {manage && (
            <Button onClick={() => setEditing((e) => !e)} aria-expanded={editing}>
              {editing ? "Close editor" : "Edit sheets"}
            </Button>
          )}
          {manage && dirty && (
            <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
              Save sheets
            </Button>
          )}
        </div>
        {sheet && (
          <p className="mb-3 text-sm text-muted">
            Print at 100% scale ("Actual size") on {PRINT_PAPERS[sheet.paper].label} paper, {sheet.landscape ? "landscape" : "portrait"}.
          </p>
        )}
        {save.error ? <Alert>{errorMessage(save.error)}</Alert> : null}
        {editing && <SheetEditor sheets={sheets} setSheets={setSheets} at={at} setAt={setAt} data={data} />}
      </div>

      {sheet && (
        <>
          <Preview w={w} h={h} fontScale={fontScale} setFontScale={setFontScale} settled={settled}>
            <Sheet sheet={sheet} data={data} fontScale={fontScale} />
          </Preview>
          {createPortal(
            <div className="vaulted-print-root">
              <Sheet sheet={sheet} data={data} fontScale={fontScale} />
            </div>,
            document.body,
          )}
          <style>{`
            @page { size: ${w}in ${h}in; margin: 0; }
            .vaulted-print-root { display: none; }
            @media print {
              html, body { margin: 0 !important; padding: 0 !important; height: auto !important; min-height: 0 !important; }
              body > *:not(.vaulted-print-root) { display: none !important; }
              /* A hair under the page, so rounding never spills onto a blank second page. */
              .vaulted-print-root { display: block; width: ${w}in; height: calc(${h}in - 2px); overflow: hidden; }
              .vaulted-print-root > div { height: calc(${h}in - 2px) !important; }
            }
          `}</style>
        </>
      )}

      <div className="mt-6 print:hidden">
        <StockCheck sheets={sheets} data={data} inStock={view.inStock} />
      </div>
    </div>
  );
}

const MIN_FONT_SCALE = 0.7;
const MAX_FONT_SCALE = 1.6;

/** The sheet at real size, scaled down to fit the screen. Shrinks the text when it runs over; warns if it still does. */
function Preview({
  w,
  h,
  fontScale,
  setFontScale,
  settled,
  children,
}: {
  w: number;
  h: number;
  fontScale: number;
  setFontScale: (n: number) => void;
  /** Done growing: it ran over once, so it only shrinks from here. */
  settled: { current: boolean };
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [over, setOver] = useState(false);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, el.clientWidth / (w * PX)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [w]);
  useLayoutEffect(() => {
    const body = inner.current?.querySelector<HTMLElement>("[data-sheet-body]");
    // Runs over the sheet, or a cell's text is wider than its column.
    const cellOver = [...(inner.current?.querySelectorAll<HTMLElement>("td, th") ?? [])].some((c) => c.scrollWidth > c.clientWidth + 1);
    const runsOver = !!body && (cellOver || body.scrollWidth > body.clientWidth + 1 || body.scrollHeight > body.clientHeight + 1);
    const step = (d: number) => setFontScale(Math.round((fontScale + d) * 100) / 100);
    if (runsOver && fontScale > MIN_FONT_SCALE + 0.001) {
      settled.current = true;
      step(-0.05);
    } else if (!runsOver && !settled.current && fontScale < MAX_FONT_SCALE - 0.001) step(0.05);
    else setOver(runsOver);
  });
  return (
    <div className="print:hidden">
      {over ? (
        <Alert>This doesn't fit on one sheet, even with smaller text. Try more columns, bigger paper, or move a category to another sheet.</Alert>
      ) : fontScale !== 1 ? (
        <p className="text-sm text-muted">Text at {Math.round(fontScale * 100)}% to fill the sheet.</p>
      ) : null}
      <div ref={box} className="mt-2 w-full">
        <div style={{ width: w * PX * scale, height: h * PX * scale }} className="overflow-hidden rounded border border-line shadow-sm">
          <div
            ref={inner}
            style={{
              width: w * PX,
              height: h * PX,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Row {
  key: string;
  name: string;
  drug: string;
  route: string;
  strength: string;
  dose: string;
  ml: string;
  sub?: boolean;
  /** The drug name only repeats the medication's name (Glutathione, Glutathione). */
  repeats?: boolean;
  /** Which medication it belongs to (for the alternating shading). */
  band: number;
}

/** Prices without cents when whole ("$195"), for room on the sheet. */
const priceText = (cents: number) => (cents % 100 ? money(cents) : money(cents).replace(/\.00$/, ""));

/** Short route names, so the column stays narrow and the text can be bigger. */
const PRINT_ROUTES: Record<Route, string> = { iv: "IV", iv_push: "IVP", im: "IM", sq: "SQ", im_sq: "IM/SQ" };

/** One line per medication; a mix (B-Complex) also lists what's in the dose. */
function rowsFor(recipe: RecipeLine[], formulary: FormularyItem[]): { rows: Row[]; fluids: string[] } {
  const rows: Row[] = [];
  const fluids: string[] = [];
  for (const l of recipe) {
    const f = formulary.find((x) => x.id === l.formularyItemId);
    if (!f) continue;
    if (f.kind === "fluid") fluids.push(f.doseUnit === "each" && l.dose === 1 ? f.name : `${f.name} ${formatDose(l.dose, f.doseUnit)}`);
    if (f.kind !== "medication") continue;
    const ml = doseToMl(f, l.dose);
    const route = l.route ? PRINT_ROUTES[l.route] : "";
    rows.push({
      key: f.id,
      name: f.name,
      drug: f.drugName || (f.components.length ? "Mix" : ""),
      repeats: f.drugName.trim().toLowerCase() === f.name.trim().toLowerCase(),
      route,
      strength: f.strengthPerMl !== null ? `${num.format(f.strengthPerMl)} ${DOSE_UNIT_LABELS[f.doseUnit]}/mL` : "",
      // Measured in mL (mixes): the mL column says it.
      dose: f.doseUnit === "ml" ? "" : formatDose(l.dose, f.doseUnit),
      ml: ml === null ? "" : `${num.format(ml)} mL`,
      band: rows.filter((r) => !r.sub).length,
    });
    if (ml !== null && f.components.length) {
      for (const [i, c] of componentDoses(f, ml).entries()) {
        rows.push({
          key: `${f.id}-${i}`,
          name: "",
          drug: c.name,
          route: "",
          strength: `${num.format(f.components[i]!.amountPerMl)} ${DOSE_UNIT_LABELS[c.unit]}/mL`,
          dose: formatDose(c.amount, c.unit),
          ml: "",
          sub: true,
          band: rows.filter((r) => !r.sub).length - 1,
        });
      }
    }
  }
  return { rows, fluids };
}

/** Colors: the clinic's brand behind titles and list headers, and every other line lightly shaded. */
const ZEBRA = "bg-[color-mix(in_srgb,var(--color-brand)_8%,white)]";
/** Box titles and list-table headers: solid brand with white text, so they stand out at a glance. */
const TITLE = "bg-[var(--color-brand)] text-white";
/** Box outlines: one weight and color everywhere. */
const EDGE = "border-[1pt] border-[color-mix(in_srgb,var(--color-brand)_60%,black)]";
const TH = "border-b-[0.75pt] border-[color-mix(in_srgb,var(--color-brand)_60%,black)] px-[calc(4pt*var(--fs))] py-[calc(2pt*var(--fs))] text-left font-semibold overflow-hidden";
/** A hairline between lines (the first under the header is the header's own). */
const LINE = "border-t-[0.5pt] border-[color-mix(in_srgb,var(--color-brand)_25%,white)] first:border-t-0";
/** Same column widths in every box, so the columns line up down the sheet. */
function Cols({ price }: { price?: boolean }) {
  const widths = [22, 26, 9, 15, 15, 10, ...(price ? [10] : [])];
  const total = widths.reduce((a, b) => a + b, 0);
  return (
    <colgroup>
      {widths.map((w, i) => (
        <col key={i} style={{ width: `${(w / total) * 100}%` }} />
      ))}
    </colgroup>
  );
}

/** Cells clip rather than spill into the next column; text sizing backs off when any would. */
const TD = "px-[calc(4pt*var(--fs))] py-[calc(1.5pt*var(--fs))] align-top overflow-hidden [overflow-wrap:anywhere]";

/** The printed page (also the preview): real size in inches, black on white. */
function Sheet({ sheet, data, fontScale }: { sheet: PrintSheet; data: CatalogResponse; fontScale: number }) {
  // Mixes shown as one line: drop their contents.
  data = showsMixes(sheet, data) ? data : { ...data, formulary: data.formulary.map((f) => ({ ...f, components: [] })) };
  const { w, h } = size(sheet);
  const groups = sheetItems(sheet, data);
  const withPrice = groups.some((g) => g.items.some((i) => oneLine(g.category, i, data.formulary)));
  // Each box or table is a block; its height (in lines) is a guess, for arranging columns evenly.
  const blocks: Block[] = [];
  const heading = (name: string) => (
    <h2 className="mb-[calc(4pt*var(--fs))] text-[length:calc(12pt*var(--fs))] font-bold uppercase tracking-wide text-[var(--color-brand-deep)]" style={{ breakAfter: "avoid" }}>
      {name}
    </h2>
  );
  // The sheet's first heading goes across the top, so every column starts at the same height.
  const top = groups[0]?.category.name;
  for (const g of groups) {
    const list = g.items.filter((i) => oneLine(g.category, i, data.formulary));
    let first = true;
    // The category's heading goes with its first block.
    const withHeading = (key: string, node: ReactNode) => {
      const h = first && g !== groups[0];
      first = false;
      return h ? (
        <div key={`h-${key}`}>
          {heading(g.category.name)}
          {node}
        </div>
      ) : (
        node
      );
    };
    if (list.length) {
      blocks.push({ lines: list.length + 4.5, pinned: blocks.length === 0, node: withHeading(g.category.id, <OneLineTable key={`l-${g.category.id}`} items={list} formulary={data.formulary} />) });
    }
    for (const item of g.items.filter((i) => !oneLine(g.category, i, data.formulary))) {
      blocks.push({
        lines: rowsFor(item.recipe, data.formulary).rows.length + 3 + (first && g !== groups[0] ? 1.5 : 0),
        pinned: blocks.length === 0,
        node: withHeading(item.id, <MenuBox key={item.id} item={item} formulary={data.formulary} price={withPrice} />),
      });
    }
  }
  sheet.notes.forEach((n, i) => blocks.push({ lines: n.rows.length * 2 + 3, pinned: false, node: <NoteTable key={`n-${i}`} note={n} /> }));
  return (
    <div
      style={{
        width: `${w}in`,
        height: `${h}in`,
        padding: "0.4in",
        fontSize: `calc(10pt * ${fontScale})`,
        ["--fs" as string]: fontScale,
        printColorAdjust: "exact",
        WebkitPrintColorAdjust: "exact",
      }}
      className="flex flex-col overflow-hidden bg-white leading-tight text-black"
    >
      {top && heading(top)}
      {/* Plain side-by-side columns: Safari prints CSS multi-column layout as one column. */}
      <div data-sheet-body className="flex min-h-0 flex-1 items-start overflow-hidden" style={{ gap: "0.3in" }}>
        {(sheet.arrange ? arrange : inOrder)(blocks, sheet.columns).map((col, c) => (
          <div key={c} className="min-w-0 flex-1">
            {col.map((b) => b.node)}
          </div>
        ))}
      </div>
    </div>
  );
}

/** One medication (or none) and no mix: a row in the category's table instead of its own box (Boost Bar, shots). Drips (one per visit) always get a box. */
const oneLine = (_category: CategoryItem, item: CatalogItem, formulary: FormularyItem[]) => !isDrip(item, formulary) && rowsFor(item.recipe, formulary).rows.length <= 1;

function OneLineTable({ items, formulary }: { items: CatalogItem[]; formulary: FormularyItem[] }) {
  if (!items.length) return null;
  return (
    <table className={`mb-[calc(8pt*var(--fs))] w-full table-fixed border-collapse ${EDGE} text-[length:calc(9.5pt*var(--fs))]`} style={{ breakInside: "avoid" }}>
      <Cols price />
      <thead className={TITLE}>
        <tr>
          <th className={TH}>Name</th>
          <th className={TH}>Drug</th>
          <th className={TH}>Route</th>
          <th className={`${TH} text-right`}>Strength</th>
          <th className={`${TH} text-right`}>Dose</th>
          <th className={`${TH} text-right`}>mL</th>
          <th className={`${TH} text-right`}>Price</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, n) => {
          const { rows, fluids } = rowsFor(item.recipe, formulary);
          const r = rows[0];
          return (
            <tr key={item.id} className={`${n % 2 ? ZEBRA : ""} ${LINE}`} style={{ breakInside: "avoid" }}>
              <td className={`${TD} font-semibold`}>
                {item.name}
                {r && fluids.length > 0 && <div className="text-[length:calc(7.5pt*var(--fs))] font-normal">In {fluids.join(", ")}</div>}
              </td>
              {/* Only a fluid (Additional Fluids): the fluid is what's given. */}
              <td className={TD}>{r?.drug ?? (fluids.length ? fluids.join(", ") : <i>No medications in the recipe</i>)}</td>
              <td className={TD}>{r?.route}</td>
              <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{r?.strength}</td>
              <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{r?.dose}</td>
              <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{r?.ml}</td>
              <td className={`${TD} text-right font-semibold tabular-nums`}>{priceText(item.priceCents)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** `price`: the sheet also has list tables, so boxes keep an empty price column and every column lines up. */
function MenuBox({ item, formulary, price }: { item: CatalogItem; formulary: FormularyItem[]; price: boolean }) {
  const { rows, fluids } = rowsFor(item.recipe, formulary);
  return (
    <div className={`mb-[calc(8pt*var(--fs))] ${EDGE}`} style={{ breakInside: "avoid" }}>
      <div className={`flex items-baseline justify-between gap-2 ${TITLE} px-[calc(4pt*var(--fs))] py-[calc(3pt*var(--fs))]`}>
        <span className="text-[length:calc(12pt*var(--fs))] font-bold">{item.name}</span>
        <span className="text-[length:calc(12pt*var(--fs))] font-bold tabular-nums">{priceText(item.priceCents)}</span>
      </div>
      {fluids.length > 0 && <div className="px-[calc(4pt*var(--fs))] pt-[calc(2pt*var(--fs))] text-[length:calc(8.5pt*var(--fs))]">In {fluids.join(", ")}</div>}
      {rows.length === 0 ? (
        <div className="px-[calc(4pt*var(--fs))] py-[calc(3pt*var(--fs))] italic">No medications in the recipe.</div>
      ) : (
        <table className="w-full table-fixed border-collapse text-[length:calc(9.5pt*var(--fs))]">
          <Cols price={price} />
          <thead>
            <tr>
              <th className={TH}>Name</th>
              <th className={TH}>Drug</th>
              <th className={TH}>Route</th>
              <th className={`${TH} text-right`}>Strength</th>
              <th className={`${TH} text-right`}>Dose</th>
              <th className={`${TH} text-right`}>mL</th>
              {price && <th className={TH} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={`${r.band % 2 ? ZEBRA : ""} ${r.sub ? "" : LINE}`}>
                <td className={`${TD} font-semibold`}>{r.name}</td>
                <td className={`${TD} ${r.sub ? "pl-[calc(10pt*var(--fs))]" : ""}`}>{r.repeats ? "" : r.drug}</td>
                <td className={TD}>{r.route}</td>
                <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{r.strength}</td>
                <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{r.dose}</td>
                <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{r.ml}</td>
                {price && <td className={TD} />}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NoteTable({ note }: { note: PrintNote }) {
  return (
    <div className={`mb-[calc(8pt*var(--fs))] ${EDGE}`} style={{ breakInside: "avoid" }}>
      <div className={`${TITLE} px-[calc(4pt*var(--fs))] py-[calc(3pt*var(--fs))] text-[length:calc(12pt*var(--fs))] font-bold`}>{note.title}</div>
      <table className="w-full border-collapse text-[length:calc(9.5pt*var(--fs))]">
        <thead>
          <tr>
            <th className={TH}>Route</th>
            <th className={TH}>Starting / typical dose</th>
            <th className={TH}>Freq</th>
            <th className={TH}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {note.rows.map((r, i) => (
            <tr key={i} className={`${i % 2 ? ZEBRA : ""} ${LINE}`}>
              <td className={`${TD} font-semibold`}>{r.route}</td>
              <td className={TD}>{r.dose}</td>
              <td className={TD}>{r.freq}</td>
              <td className={TD}>{r.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** What's on the shelf but not on the printed menu, and what's printed but out of stock. */
function StockCheck({ sheets, data, inStock }: { sheets: PrintSheet[]; data: CatalogResponse; inStock: string[] }) {
  const printed = sheets.flatMap((s) => sheetItems(s, data).flatMap((g) => g.items));
  const printedIds = new Set(printed.flatMap((i) => i.recipe.map((l) => l.formularyItemId)));
  const live = data.items.filter((i) => i.status === "active");
  const meds = data.formulary.filter((f) => f.kind === "medication" && !f.clinicalOnly);
  const stocked = meds.filter((f) => inStock.includes(f.id) && !printedIds.has(f.id));
  const nowhere = stocked.filter((f) => !live.some((i) => i.recipe.some((l) => l.formularyItemId === f.id)));
  const notPrinted = stocked.filter((f) => !nowhere.includes(f));
  const out = meds.filter((f) => printedIds.has(f.id) && !inStock.includes(f.id));
  const usedBy = (f: FormularyItem, list: CatalogItem[]) => list.filter((i) => i.recipe.some((l) => l.formularyItemId === f.id)).map((i) => i.name);
  const List = ({ title, hint, items, detail }: { title: string; hint: string; items: FormularyItem[]; detail?: (f: FormularyItem) => string }) => (
    <div>
      <h3 className="font-semibold">
        {title} <span className="font-normal text-muted">({items.length})</span>
      </h3>
      <p className="text-sm text-muted">{hint}</p>
      {items.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-sm">
          {items.map((f) => (
            <li key={f.id}>
              <span className="font-semibold">{f.name}</span>
              {detail?.(f) && <span className="text-ink-soft"> · {detail(f)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <Panel>
      <PanelHeader title="Stock check" description="Medications compared with what these sheets print. Not printed." />
      <div className="grid gap-5 px-5 py-5 md:grid-cols-3">
        <List
          title="In stock, not on any menu item"
          hint="Add a service or add-on for it, or add it to a recipe."
          items={nowhere}
          detail={(f) => {
            const same = live.find((i) => i.name.trim().toLowerCase() === f.name.trim().toLowerCase() && i.recipe.length === 0);
            return same ? `"${same.name}" has no recipe yet` : "";
          }}
        />
        <List title="In stock, on items not printed" hint="Hidden from the menu, or in a category no sheet prints." items={notPrinted} detail={(f) => usedBy(f, live).join(", ")} />
        <List title="Printed, but out of stock" hint="On the menu with nothing on the shelf." items={out} detail={(f) => usedBy(f, printed).join(", ")} />
      </div>
    </Panel>
  );
}

interface Block {
  lines: number;
  /** The sheet's first block stays at the top of the first column. */
  pinned: boolean;
  node: ReactNode;
}

/** Spread blocks over the columns so they come out about even (tallest first, into the shortest column); each column stays A to Z. */
function arrange(blocks: Block[], columns: number): Block[][] {
  const cols: { at: number[]; lines: number }[] = Array.from({ length: columns }, () => ({ at: [], lines: 0 }));
  const order = blocks.map((_, i) => i).sort((a, b) => Number(blocks[b]!.pinned) - Number(blocks[a]!.pinned) || blocks[b]!.lines - blocks[a]!.lines);
  for (const i of order) {
    const col = blocks[i]!.pinned ? cols[0]! : cols.reduce((m, c) => (c.lines < m.lines ? c : m));
    col.at.push(i);
    col.lines += blocks[i]!.lines;
  }
  return cols.map((c) => c.at.sort((a, b) => a - b).map((i) => blocks[i]!));
}

/** Blocks in menu order down the first column, then the next, splitting about evenly. */
function inOrder(blocks: Block[], columns: number): Block[][] {
  const total = blocks.reduce((n, b) => n + b.lines, 0);
  const cols: Block[][] = Array.from({ length: columns }, () => []);
  let before = 0;
  for (const b of blocks) {
    // The column its middle falls in.
    cols[Math.min(columns - 1, Math.floor(((before + b.lines / 2) / total) * columns))]!.push(b);
    before += b.lines;
  }
  return cols;
}

function SheetEditor({ sheets, setSheets, at, setAt, data }: { sheets: PrintSheet[]; setSheets: (s: PrintSheet[]) => void; at: number; setAt: (i: number) => void; data: CatalogResponse }) {
  const i = Math.min(at, sheets.length - 1);
  const s = sheets[i];
  const change = (patch: Partial<PrintSheet>) => setSheets(sheets.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const elsewhere = (catId: string) => sheets.find((x, j) => j !== i && x.categoryIds.includes(catId))?.name;
  const add = () => {
    setSheets([
      ...sheets,
      {
        id: crypto.randomUUID(),
        name: `Sheet ${sheets.length + 1}`,
        paper: "letter",
        landscape: false,
        columns: 2,
        categoryIds: [],
        includeHidden: false,
        notes: [],
      },
    ]);
    setAt(sheets.length);
  };
  const setNote = (n: number, note: PrintNote | null) =>
    change({
      notes: note ? s!.notes.map((x, k) => (k === n ? note : x)) : s!.notes.filter((_, k) => k !== n),
    });
  return (
    <Panel className="mb-4">
      <PanelHeader
        title={s ? `Edit "${s.name}"` : "Sheets"}
        action={
          <div className="flex gap-2">
            {sheets.length < 6 && <Button onClick={add}>Add sheet</Button>}
            {s && (
              <Button variant="danger" onClick={() => (setSheets(sheets.filter((_, j) => j !== i)), setAt(0))}>
                Remove sheet
              </Button>
            )}
          </div>
        }
      />
      {s && (
        <div className="flex flex-col gap-4 px-5 py-5">
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Name" maxLength={60} value={s.name} onChange={(e) => change({ name: e.target.value })} />
            <SelectField label="Paper" value={s.paper} onChange={(e) => change({ paper: e.target.value as PrintPaper })}>
              {Object.entries(PRINT_PAPERS).map(([k, p]) => (
                <option key={k} value={k}>
                  {p.label}
                </option>
              ))}
            </SelectField>
            <SelectField label="Direction" value={s.landscape ? "landscape" : "portrait"} onChange={(e) => change({ landscape: e.target.value === "landscape" })}>
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </SelectField>
            <SelectField label="Columns" value={s.columns} onChange={(e) => change({ columns: Number(e.target.value) })}>
              {[1, 2, 3, 4].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </SelectField>
          </div>
          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Categories on this sheet</legend>
            <div className="grid gap-x-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.categories.map((c) => (
                <Checkbox
                  key={c.id}
                  label={c.name}
                  description={elsewhere(c.id) ? `Also on ${elsewhere(c.id)}` : undefined}
                  checked={s.categoryIds.includes(c.id)}
                  onChange={(on) =>
                    change({
                      categoryIds: on ? [...s.categoryIds, c.id] : s.categoryIds.filter((x) => x !== c.id),
                    })
                  }
                />
              ))}
            </div>
          </fieldset>
          <Checkbox label="Include items hidden from the public menu" checked={s.includeHidden} onChange={(on) => change({ includeHidden: on })} />
          <Checkbox
            label="Show what's in each mix"
            description="B-Complex, Lipo-Mino, and other mixes list their ingredients under them. Off: one line each."
            checked={showsMixes(s, data)}
            onChange={(on) => change({ showMixes: on })}
          />
          <Checkbox
            label="Arrange to fit"
            description="Place the boxes so the columns come out even, for bigger text. Each column stays A to Z."
            checked={!!s.arrange}
            onChange={(on) => change({ arrange: on })}
          />
          <div className="flex flex-col gap-3">
            <div className="text-sm font-semibold text-ink-soft">Dosing tables (printed after the menu)</div>
            {s.notes.map((n, k) => (
              <div key={k} className="rounded-[var(--radius-control)] border border-line p-3">
                <div className="flex items-end gap-2">
                  <Field label="Title" className="flex-1" maxLength={80} value={n.title} onChange={(e) => setNote(k, { ...n, title: e.target.value })} />
                  {sheets.length > 1 && (
                    <SelectField
                      label="Print on"
                      className="w-56"
                      value={i}
                      onChange={(e) => {
                        const to = Number(e.target.value);
                        setSheets(sheets.map((x, j) => (j === i ? { ...x, notes: x.notes.filter((_, nk) => nk !== k) } : j === to ? { ...x, notes: [...x.notes, n] } : x)));
                      }}
                    >
                      {sheets.map((x, j) => (
                        <option key={x.id} value={j}>
                          {x.name}
                        </option>
                      ))}
                    </SelectField>
                  )}
                  <RemoveButton label={`Remove ${n.title}`} onClick={() => setNote(k, null)} />
                </div>
                <table className="table-base mt-2">
                  <thead>
                    <tr>
                      <th>Route</th>
                      <th>Starting / typical dose</th>
                      <th>Freq</th>
                      <th>Notes</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {n.rows.map((r, ri) => {
                      const cell = (key: keyof typeof r, max: number, wide?: boolean) => (
                        <td>
                          <input
                            aria-label={key}
                            maxLength={max}
                            className={`${wide ? "w-full min-w-40" : "w-24"} rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5`}
                            value={r[key]}
                            onChange={(e) =>
                              setNote(k, {
                                ...n,
                                rows: n.rows.map((x, xi) => (xi === ri ? { ...x, [key]: e.target.value } : x)),
                              })
                            }
                          />
                        </td>
                      );
                      return (
                        <tr key={ri}>
                          {cell("route", 40)}
                          {cell("dose", 200, true)}
                          {cell("freq", 40)}
                          {cell("notes", 300, true)}
                          <td>
                            <RemoveButton
                              label="Remove row"
                              onClick={() =>
                                setNote(k, {
                                  ...n,
                                  rows: n.rows.filter((_, xi) => xi !== ri),
                                })
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {n.rows.length < 20 && (
                  <Button
                    variant="quiet"
                    className="mt-1"
                    onClick={() =>
                      setNote(k, {
                        ...n,
                        rows: [...n.rows, { route: "", dose: "", freq: "", notes: "" }],
                      })
                    }
                  >
                    + Row
                  </Button>
                )}
              </div>
            ))}
            {s.notes.length < 5 && (
              <Button
                className="self-start"
                onClick={() =>
                  change({
                    notes: [
                      ...s.notes,
                      {
                        title: "Dosing",
                        rows: [{ route: "", dose: "", freq: "", notes: "" }],
                      },
                    ],
                  })
                }
              >
                Add dosing table
              </Button>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
